import { useMemo, useState } from 'react';
import {
	Badge,
	Box,
	Button,
	Flex,
	Grid,
	HStack,
	Input,
	InputGroup,
	InputLeftElement,
	NumberInput,
	NumberInputField,
	Select,
	SimpleGrid,
	Spinner,
	Text,
} from '@chakra-ui/react';
import { RiAddLine, RiDeleteBinLine, RiEyeLine, RiSearchLine, RiSmartphoneLine } from 'react-icons/ri';

import useAppToast from 'components/toast';
import { Banner, SectionCard, StatusBadge } from '../_ui/components';
import { useCampaignUI } from '../_ui/ui';
import { useListMetaWhatsappTemplatesQuery, useRenderSampleMutation } from 'api/metaWhatsappApi';
// ★ Shared with the inbox picker AND mirrored from the server's own
// `requiredKeys`. This file used to keep its own copy, which asked for a header
// value whenever the kind was anything but 'none' — including a static header
// with no placeholder, for which Meta then rejected the send.
import { requiredKeys, describeKey } from 'views/admin/meta-whatsapp/_components/templateVariables';
import { partsOf, substitute, fillsFrom, excerpt } from 'views/admin/meta-whatsapp/_components/templateParts';
import TemplateBubble from 'views/admin/meta-whatsapp/_components/TemplateBubble';
import campaignError from '../campaignErrors';
import DripSteps from './DripSteps';
import VariantOptimisation from './VariantOptimisation';
import { ChoiceCard } from '../_ui/controls';

/**
 * What the customer receives.
 *
 * ★ THE PHONE IS ON SCREEN THE WHOLE TIME. Picking a template from a dropdown
 * by its Meta name — `crm_vars_body_1789814532639` — told nobody what the
 * customer would read. The picker now shows each template's words, and the
 * chosen one is drawn as a WhatsApp bubble beside the editor, re-rendered as
 * every variable is mapped. What you approve is what you see.
 *
 * ★ ONLY APPROVED TEMPLATES ARE OFFERED. WhatsApp refuses anything else, so a
 * picker that listed a pending or rejected template would be a menu of things
 * that cannot be sent. The mirror's status is the server's, refreshed from Meta.
 *
 * ★ EVERY VARIABLE NEEDS A FALLBACK, AND THE FORM INSISTS. WhatsApp rejects an
 * empty parameter outright, and a message reading "Hi ," is worse than one
 * reading "Hi there,". The fallback is what the recipient sees when the CRM has
 * nothing — so it is a required field, not a nicety.
 *
 * ★ VARIANTS ARE WEIGHTED, NOT SPLIT EVENLY. Weights are relative — 3 and 1 is
 * three-quarters to one — and which variant a person gets is decided by a hash
 * of their number, so a re-plan never moves anybody between variants.
 */

/** The variables the server can fill, grouped the way a person thinks. */
const SOURCES = [
	{ group: 'Lead', options: [
		['lead.first_name', 'First name'],
		['lead.full_name', 'Full name'],
		['lead.city', 'City'],
		['lead.country', 'Country'],
		['lead.interest', 'Interest'],
		['lead.budget', 'Budget'],
		['lead.client_id', 'Client ID (lead number)'],
	] },
	{ group: 'Agent', options: [
		['agent.name', 'Assigned agent name'],
		['agent.phone', 'Assigned agent phone'],
	] },
	{ group: 'Campaign', options: [
		['campaign.name', 'Campaign name'],
		['campaign.today', "Today's date, where they are"],
	] },
];

/** source id → label, for the bubble. */
const SOURCE_LABELS = Object.fromEntries(SOURCES.flatMap((g) => g.options));

const keyOf = (t) => `${t.name}||${t.language}`;

const newVariantId = (existing) => {
	const used = new Set(existing.map((v) => v.id));
	for (const letter of 'abcdefghijklmnop') if (!used.has(letter)) return letter;
	return `v${existing.length + 1}`;
};

/** Which template a variant points at, or null. */
const templateFor = (variant, templates) =>
	templates.find((t) => t.name === variant?.templateRef?.name && t.language === variant?.templateRef?.language) ?? null;

/** The bubble's four parts for a variant, with its mappings substituted in. Shared with Review. */
export function previewOf(variant, templates) {
	const template = templateFor(variant, templates);
	if (!template) return null;
	const parts = partsOf(template);
	const fills = fillsFrom(requiredKeys(template), variant.paramMap ?? {}, SOURCE_LABELS);
	return {
		...parts,
		header: substitute(parts.header, fills),
		body: substitute(parts.body, fills),
	};
}

/** Templates as cards you can read, not names you have to decode. */
function TemplatePicker({ templates, value, onChange }) {
	const ui = useCampaignUI();
	const [q, setQ] = useState('');
	const shown = useMemo(() => {
		const needle = q.trim().toLowerCase();
		if (!needle) return templates;
		return templates.filter((t) => {
			const { body } = partsOf(t);
			return `${t.name} ${t.language} ${t.category ?? ''} ${body}`.toLowerCase().includes(needle);
		});
	}, [templates, q]);

	return (
		<Box>
			{templates.length > 4 && (
				<InputGroup size='sm' mb={3}>
					<InputLeftElement pointerEvents='none' color={ui.iconMuted}>
						<RiSearchLine />
					</InputLeftElement>
					<Input
						value={q}
						onChange={(e) => setQ(e.target.value)}
						placeholder='Find a template by name or words…'
						bg={ui.inputBg}
						borderColor={ui.border}
						color={ui.textPrimary}
					/>
				</InputGroup>
			)}
			{!shown.length ? (
				<Text fontSize='sm' color={ui.textMuted}>
					Nothing matches “{q}”.
				</Text>
			) : (
				<SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} role='radiogroup' aria-label='Template'>
					{shown.map((t) => {
						const { body } = partsOf(t);
						const vars = requiredKeys(t).length;
						return (
							<ChoiceCard
								key={keyOf(t)}
								selected={value === keyOf(t)}
								onSelect={() => onChange(t)}
								title={t.name}
								description={
									<>
										<Box as='span' display='block' mb={1.5}>
											{excerpt(body) || 'No body text.'}
										</Box>
										<Badge variant='subtle' fontSize='0.6rem' mr={1}>
											{t.language}
										</Badge>
										<Badge variant='subtle' fontSize='0.6rem' mr={1} textTransform='capitalize'>
											{String(t.category ?? '').toLowerCase()}
										</Badge>
										<Badge variant='subtle' fontSize='0.6rem'>
											{vars ? `${vars} variable${vars === 1 ? '' : 's'}` : 'no variables'}
										</Badge>
									</>
								}
							/>
						);
					})}
				</SimpleGrid>
			)}
		</Box>
	);
}

export function VariantEditor({ variant, templates, onChange, onRemove, canRemove, showPicker = true }) {
	const ui = useCampaignUI();
	const template = templateFor(variant, templates);
	const keys = requiredKeys(template);
	const paramMap = variant.paramMap ?? {};

	const setTemplate = (picked) => {
		onChange({
			...variant,
			templateRef: { wabaId: picked?.wabaId, name: picked.name, language: picked.language },
			// A different template needs different parameters; carrying the old
			// map over would leave a mapping for a variable that no longer exists.
			paramMap: {},
		});
	};

	const setParam = (key, field, value) =>
		onChange({
			...variant,
			paramMap: { ...paramMap, [key]: { ...(paramMap[key] ?? {}), [field]: value } },
		});

	return (
		<Box borderWidth='1px' borderColor={ui.border} borderRadius='xl' p={{ base: 3, md: 4 }} mb={4} bg={ui.surface}>
			<Flex justify='space-between' align='center' mb={3} gap={3} wrap='wrap'>
				<HStack spacing={3}>
					<Text fontSize='sm' fontWeight='semibold' color={ui.textPrimary}>
						Variant {variant.id.toUpperCase()}
					</Text>
					{template && <StatusBadge status={template.status} />}
				</HStack>
				<HStack spacing={2}>
					<Text fontSize='xs' color={ui.textMuted}>
						Weight
					</Text>
					<NumberInput
						size='sm'
						maxW='72px'
						min={0}
						value={variant.weight ?? 1}
						onChange={(v) => onChange({ ...variant, weight: Number(v) || 0 })}
					>
						<NumberInputField bg={ui.inputBg} borderColor={ui.border} color={ui.textPrimary} />
					</NumberInput>
					{canRemove && (
						<Button size='xs' variant='ghost' color={ui.danger} onClick={onRemove} aria-label='Remove variant'>
							<RiDeleteBinLine />
						</Button>
					)}
				</HStack>
			</Flex>

			{showPicker && (
				<Box mb={template ? 4 : 0}>
					<TemplatePicker templates={templates} value={template ? keyOf(template) : ''} onChange={setTemplate} />
				</Box>
			)}

			{template && keys.length === 0 && (
				<Text fontSize='xs' color={ui.textMuted}>
					This template has no variables — everyone receives the same words.
				</Text>
			)}

			{template && keys.length > 0 && (
				<Box>
					<Text fontSize='xs' fontWeight='semibold' color={ui.textSecondary} mb={2} textTransform='uppercase' letterSpacing='wide'>
						Fill in the blanks
					</Text>
					{keys.map((key) => (
						<Grid
							key={key}
							templateColumns={{ base: '1fr', md: '120px minmax(0,1fr) minmax(0,1fr)' }}
							gap={2}
							mb={2}
							alignItems='center'
						>
							<Text fontSize='xs' color={ui.textMuted} fontFamily='mono'>
								{key === 'header' ? 'Header' : key.startsWith('button:') ? describeKey(key) : `{{${key}}}`}
							</Text>
							<Select
								size='sm'
								value={paramMap[key]?.source ?? ''}
								onChange={(e) => setParam(key, 'source', e.target.value)}
								bg={ui.inputBg}
								borderColor={ui.border}
								color={ui.textPrimary}
								aria-label={`Source for ${describeKey(key)}`}
							>
								<option value=''>Fill from…</option>
								{SOURCES.map((group) => (
									<optgroup key={group.group} label={group.group}>
										{group.options.map(([value, label]) => (
											<option key={value} value={value}>
												{label}
											</option>
										))}
									</optgroup>
								))}
								<optgroup label='Typed for this campaign'>
									<option value='literal:'>Fixed text…</option>
								</optgroup>
							</Select>
							<Input
								size='sm'
								placeholder='If we have nothing — e.g. “there”'
								value={paramMap[key]?.fallback ?? ''}
								onChange={(e) => setParam(key, 'fallback', e.target.value)}
								bg={ui.inputBg}
								borderColor={ui.border}
								color={ui.textPrimary}
								aria-label={`Fallback for ${describeKey(key)}`}
							/>
							{String(paramMap[key]?.source ?? '').startsWith('literal:') && (
								<Input
									size='sm'
									gridColumn={{ md: '2 / 4' }}
									placeholder='The exact words to send'
									value={String(paramMap[key].source).slice('literal:'.length)}
									onChange={(e) => setParam(key, 'source', `literal:${e.target.value}`)}
									bg={ui.inputBg}
									borderColor={ui.border}
									color={ui.textPrimary}
								/>
							)}
						</Grid>
					))}
				</Box>
			)}
		</Box>
	);
}

/** The phone. Sticky beside the editor on wide screens, under it on narrow ones. */
function PhonePreview({ variants, templates, focus, onFocus }) {
	const ui = useCampaignUI();
	const variant = variants.find((v) => v.id === focus) ?? variants[0] ?? null;
	const preview = variant ? previewOf(variant, templates) : null;

	return (
		<Box position={{ base: 'static', xl: 'sticky' }} top='88px'>
			<Flex align='center' justify='space-between' mb={3} gap={2} wrap='wrap'>
				<HStack spacing={2}>
					<RiSmartphoneLine color={ui.accentInk} />
					<Text fontSize='sm' fontWeight='semibold' color={ui.textPrimary}>
						On the phone
					</Text>
				</HStack>
				{variants.length > 1 && (
					<HStack spacing={1}>
						{variants.map((v) => (
							<Button
								key={v.id}
								size='xs'
								variant={v.id === variant?.id ? 'solid' : 'ghost'}
								bg={v.id === variant?.id ? ui.accent : 'transparent'}
								color={v.id === variant?.id ? ui.textOnAccent : ui.textSecondary}
								onClick={() => onFocus(v.id)}
							>
								{v.id.toUpperCase()}
							</Button>
						))}
					</HStack>
				)}
			</Flex>

			<Box
				borderRadius='2xl'
				borderWidth='1px'
				borderColor={ui.border}
				bg={ui.surfaceSubtle}
				p={{ base: 4, md: 5 }}
				minH='300px'
				// A faint dot field, like a chat wallpaper — drawn from the border
				// token so it follows the preset.
				backgroundImage={`radial-gradient(${ui.border} 1px, transparent 1px)`}
				backgroundSize='14px 14px'
			>
				{preview ? (
					<TemplateBubble
						header={preview.header}
						headerKind={preview.headerKind}
						body={preview.body}
						footer={preview.footer}
						buttons={preview.buttons}
						stamp={`Variant ${String(variant.id).toUpperCase()} · preview`}
					/>
				) : (
					<Flex direction='column' align='center' justify='center' h='100%' minH='260px' textAlign='center' px={4}>
						<RiSmartphoneLine size={28} color={ui.iconMuted} />
						<Text fontSize='sm' color={ui.textMuted} mt={3}>
							Choose a template and it appears here as the customer will see it.
						</Text>
					</Flex>
				)}
			</Box>
			<Text fontSize='xs' color={ui.textMuted} mt={2}>
				Bracketed words like <Box as='span' fontFamily='mono'>[First name]</Box> are filled from the CRM for each
				person. Use <em>Preview</em> to see three real recipients.
			</Text>
		</Box>
	);
}

export default function StepMessage({ campaign, draft, patch }) {
	const ui = useCampaignUI();
	const notify = useAppToast();

	// The route lists the tenant's whole catalogue when no channel is named —
	// a campaign is not scoped to one channel; each variant carries its own
	// `templateRef.wabaId` and the sender pool resolves the rest at send time.
	const { data: allTemplates, isLoading } = useListMetaWhatsappTemplatesQuery({ status: 'APPROVED' });
	const [renderSample, { isLoading: isRendering }] = useRenderSampleMutation();
	const [samples, setSamples] = useState(null);
	const [focus, setFocus] = useState(null);

	const templates = useMemo(() => allTemplates ?? [], [allTemplates]);
	const variants = draft.variants ?? [];

	const setVariants = (next) => patch({ variants: next });

	const onPreview = async () => {
		try {
			const result = await renderSample({ id: campaign._id, size: 3 }).unwrap();
			setSamples(result.samples ?? []);
			if (!result.samples?.length) {
				notify.info('Nothing to preview yet', 'Previews use real recipients, so build the audience first.');
			}
		} catch (err) {
			notify.error('Could not render a preview', campaignError(err));
		}
	};

	if (isLoading) {
		return (
			<Flex justify='center' py={12}>
				<Spinner color={ui.accentInk} />
			</Flex>
		);
	}

	return (
		<Box>
			{!templates.length && (
				<Box mb={5}>
					<Banner
						status='warning'
						title='No approved templates'
						description='WhatsApp must approve a template before it can be sent. Create one in your WhatsApp Manager, then sync from the Meta WhatsApp settings tab.'
					/>
				</Box>
			)}

			{/* ★ SIDE-BY-SIDE ONLY FROM `xl`. Breakpoints measure the viewport, not
			    this column: at `lg` (992px) the sidebar leaves roughly 400px here,
			    and a 340px phone beside that crushed the picker to one word per
			    line. Below `xl` the phone sits under the editor at full width. */}
			<Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) 340px' }} gap={{ base: 5, xl: 6 }} alignItems='start'>
				<Box minW={0}>
					<SectionCard
						title='The message'
						subtitle='Pick the template the customer receives. Add a second variant to test two wordings — each person always gets the same one.'
						actions={
							<HStack spacing={2}>
								<Button
									size='sm'
									leftIcon={<RiEyeLine />}
									variant='neutralOutline'
									borderColor={ui.border}
									color={ui.textPrimary}
									isLoading={isRendering}
									onClick={onPreview}
								>
									Preview
								</Button>
								<Button
									size='sm'
									leftIcon={<RiAddLine />}
									variant='neutralOutline'
									borderColor={ui.border}
									color={ui.textPrimary}
									isDisabled={!templates.length}
									onClick={() => setVariants([...variants, { id: newVariantId(variants), weight: 1, paramMap: {} }])}
								>
									Add variant
								</Button>
							</HStack>
						}
					>
						{!variants.length ? (
							// The first choice IS the picker — no button standing between
							// the person and the templates.
							<TemplatePicker
								templates={templates}
								value=''
								onChange={(t) =>
									setVariants([
										{ id: 'a', weight: 1, paramMap: {}, templateRef: { wabaId: t.wabaId, name: t.name, language: t.language } },
									])
								}
							/>
						) : (
							variants.map((variant, index) => (
								<Box key={variant.id} onFocusCapture={() => setFocus(variant.id)}>
									<VariantEditor
										variant={variant}
										templates={templates}
										canRemove={variants.length > 1}
										onChange={(next) => setVariants(variants.map((v, i) => (i === index ? next : v)))}
										onRemove={() => setVariants(variants.filter((_, i) => i !== index))}
									/>
								</Box>
							))
						)}

						{/* Sits with the variants because it is a decision ABOUT them:
						    whether the split the operator just set is allowed to move. */}
						<VariantOptimisation
							value={draft.variantOptimisation ?? {}}
							variantCount={variants.length}
							onChange={(next) => patch({ variantOptimisation: next })}
						/>

						{/* A drip's later messages are still messages, so they are chosen
						    here rather than on the schedule step — the delay is the small
						    part, the template is the decision. */}
						<DripSteps
							steps={draft.drip?.steps ?? []}
							templates={templates}
							onChange={(steps) => patch({ drip: { ...(draft.drip ?? {}), steps } })}
						/>
					</SectionCard>

					{samples?.length ? (
						<SectionCard title='What three real recipients will receive' mt={5}>
							{samples.map((sample) => (
								<Box
									key={String(sample.recipientId)}
									borderWidth='1px'
									borderColor={ui.borderSubtle}
									borderRadius='lg'
									p={3}
									mb={3}
									bg={ui.surfaceSubtle}
								>
									<Text fontSize='xs' color={ui.textMuted} mb={1}>
										+{sample.waId} · variant {String(sample.variantId ?? '').toUpperCase()}
									</Text>
									{sample.error ? (
										<Text fontSize='sm' color={ui.danger}>
											{campaignError(sample.error)}
										</Text>
									) : (
										<Text fontSize='sm' color={ui.textPrimary}>
											{Object.entries(sample.renderedParams ?? {})
												.map(([key, value]) => `${key}: ${value}`)
												.join('  ·  ') || 'No variables — the template’s own words.'}
										</Text>
									)}
								</Box>
							))}
						</SectionCard>
					) : null}
				</Box>

				<PhonePreview variants={variants} templates={templates} focus={focus} onFocus={setFocus} />
			</Grid>
		</Box>
	);
}
